import { useState } from "react";
import { RotateCw } from "lucide-react";
import { Button } from "@/components/ui";

// RotatePrompt asks a TABLET held in portrait to turn sideways.
//
// It asks rather than forces because nothing in a browser can force rotation on
// the device that needs it most: `screen.orientation.lock()` requires fullscreen
// and is not implemented by Safari on iOS/iPadOS at all, and the manifest's
// `orientation: landscape` (which Klutch does ship) is honoured by Android but
// ignored by iOS. A prompt is the only mechanism that works everywhere.
//
// WHO SEES IT is the whole design, and it is a CSS media query rather than a
// width check in JS (see `.rotate-prompt` in index.css):
//
//   - `pointer: coarse` limits it to touch devices, so a desktop browser in a
//     tall narrow window - a perfectly reasonable way to use the app - is never
//     interrupted;
//   - `min-width: 600px` limits it to tablets. A phone in portrait is the right
//     way to hold a phone, and the app has a drawer nav and sm: breakpoints for
//     exactly that; telling someone to turn their phone sideways to read a job
//     card would be worse than the portrait layout ever is.
//
// And it is dismissible. A hard block with no way past it is hostile when
// someone has a reason - checking one figure one-handed - and the dismissal
// lasts the session, so it does not nag on every navigation.
const DISMISS_KEY = "klutch-rotate-dismissed";

export function RotatePrompt() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      // Private browsing can throw on sessionStorage; a prompt that cannot
      // remember a dismissal is still better than one that crashes the app.
      return false;
    }
  });

  if (dismissed) return null;

  function dismiss() {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* not remembering is fine; the state below still hides it for now */
    }
    setDismissed(true);
  }

  return (
    <div className="rotate-prompt fixed inset-0 z-100 hidden flex-col items-center justify-center gap-5 bg-bg p-10 text-center">
      <span className="grid size-16 place-items-center rounded-2xl bg-amber-bg text-amber-ink">
        <RotateCw size={30} />
      </span>
      <div className="flex flex-col gap-2">
        <h1 className="font-head text-[22px] font-bold text-ink">Turn your tablet sideways</h1>
        <p className="max-w-sm text-[14px] leading-relaxed text-muted">
          Klutch's job cards, invoices and stock tables are laid out for a landscape screen.
          Rotating gives you the full table instead of a squeezed one.
        </p>
      </div>
      <Button variant="secondary" onClick={dismiss}>
        Continue in portrait
      </Button>
    </div>
  );
}
